import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    points: Array,
    color: { type: String, default: "" },
    lineWidth: { type: Number, default: 1.5 },
    rotation: { type: Number, default: 0 },
  }

  connect() {
    const pts = this.pointsValue
    if (!pts || pts.length < 2) return

    // center_line tuples are [x, y, z, wL, wR] — use x (index 0) and z (index 2) for top-down view.
    // Presentation rotation (tracks.map_rotation_deg): rotate to [x, z] pairs around
    // the centroid so the thumbnail matches the oriented session/hero maps. Angle is
    // negated (the z-axis runs downward here, no vertical flip) to turn the same way.
    const rotXs = pts.map((p) => p[0]),
      rotZs = pts.map((p) => p[2])
    let proj = pts.map((p) => [p[0], p[2]])
    if (this.rotationValue) {
      const cx = (Math.min(...rotXs) + Math.max(...rotXs)) / 2
      const cz = (Math.min(...rotZs) + Math.max(...rotZs)) / 2
      const rad = (-this.rotationValue * Math.PI) / 180
      const cos = Math.cos(rad),
        sin = Math.sin(rad)
      proj = proj.map(([x, z]) => {
        const dx = x - cx,
          dz = z - cz
        return [cx + dx * cos - dz * sin, cz + dx * sin + dz * cos]
      })
    }
    const xs = proj.map((p) => p[0])
    const zs = proj.map((p) => p[1])
    const xExt = [Math.min(...xs), Math.max(...xs)]
    const zExt = [Math.min(...zs), Math.max(...zs)]
    const dw = xExt[1] - xExt[0] || 1
    const dh = zExt[1] - zExt[0] || 1

    const canvas = this.element
    const dpr = window.devicePixelRatio || 1
    const w = canvas.width || 40
    const h = canvas.height || 40
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`

    const pad = 3
    const s = Math.min((w - pad * 2) / dw, (h - pad * 2) / dh)
    const ox = pad + (w - pad * 2 - dw * s) / 2
    const oy = pad + (h - pad * 2 - dh * s) / 2

    const ctx = canvas.getContext("2d")
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    ctx.strokeStyle =
      this.colorValue ||
      getComputedStyle(document.documentElement)
        .getPropertyValue("--app-text-faint")
        .trim()
    ctx.lineWidth = this.lineWidthValue
    ctx.lineJoin = "round"
    ctx.lineCap = "round"
    ctx.beginPath()

    const stride = Math.max(1, Math.floor(proj.length / 300))
    for (let i = 0; i < proj.length; i += stride) {
      const x = ox + (proj[i][0] - xExt[0]) * s
      const z = oy + (proj[i][1] - zExt[0]) * s
      if (i === 0) ctx.moveTo(x, z)
      else ctx.lineTo(x, z)
    }

    ctx.closePath()
    ctx.stroke()
  }
}
